import { useEffect, useState } from "react";
import { Info, LayoutGrid, Trash2, Upload } from "lucide-react";
import { toast } from "react-toastify";
import {
  workspaceError,
  workspaceRequest,
} from "../../services/workspaceAdmin";
import { normalizeTenantRequirements } from "../../utils/tenantRequirements";
import { normalizeRequirementsConfig } from "../../utils/workspaceRequirements";
import Loading from "../loader/Loading";
import Toggle from "./WorkspaceToggle";
import WorkspaceRequirementsConfig from "./WorkspaceRequirementsConfig";
import { CDN_CONTAINER, CDN_ENDPOINT } from "../../config";

const TABS = [
  ["general", "General"],
  ["members", "Members"],
  ["invitations", "Invitations"],
  ["requirements", "Requirements"],
  ["notifications", "Notifications"],
  ["billing", "Billing"],
];
const ROLES = ["GOLD", "SILVER", "BRONZE", "GUEST"];
const AVATARS = [1, 2, 3, 4, 5, 6, 7];
export const imageUrl = (image) =>
  !image
    ? null
    : /^(\/images\/)?DefaultWorkspace\d\.png$/.test(image)
      ? `/images/${image.replace("/images/", "")}`
      : /^https?:\/\//i.test(image)
        ? image
        : `${CDN_ENDPOINT}/${CDN_CONTAINER}/${image}`;

export default function WorkspaceManager({
  landlordId,
  workspaceId,
  workspaceName,
  onRenamed,
}) {
  const [tab, setTab] = useState("general");
  const [state, setState] = useState({ key: null, data: null, error: "" });
  const [version, setVersion] = useState(0);
  const key = `${landlordId}/${workspaceId}/${version}`;
  const loading = state.key !== key;

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    workspaceRequest(landlordId, `/${workspaceId}`, {
      signal: controller.signal,
    })
      .then((data) => {
        if (active) setState({ key, data, error: "" });
      })
      .catch((error) => {
        if (active) setState({ key, data: null, error: workspaceError(error) });
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [key, landlordId, workspaceId]);

  const data = loading ? null : state.data;
  return (
    <div
      className={`lw-management-content${tab === "requirements" ? " wide" : ""}`}
    >
      <header className="lw-header">
        <div>
          <h1>Manage workspace</h1>
          <p className="lw-subtitle">
            {data?.workspace.name || workspaceName || "Unnamed workspace"}
          </p>
        </div>
      </header>
      <nav className="lw-tabs" aria-label="Workspace settings">
        {TABS.map(([value, label]) => (
          <button
            type="button"
            key={value}
            aria-current={tab === value ? "page" : undefined}
            className={tab === value ? "is-active" : ""}
            onClick={() => setTab(value)}
          >
            {label}
          </button>
        ))}
      </nav>
      {loading ? (
        <div
          className="lw-loading"
          role="status"
          aria-label="Loading workspace"
        >
          <Loading />
        </div>
      ) : state.error ? (
        <div className="lw-empty" role="alert">
          <p>{state.error}</p>
          <button
            type="button"
            className="lw-button"
            onClick={() => setVersion((value) => value + 1)}
          >
            Try again
          </button>
        </div>
      ) : (
        data && (
          <WorkspacePanel
            key={`${key}/${tab}`}
            data={data}
            landlordId={landlordId}
            workspaceId={workspaceId}
            tab={tab}
            onSaved={(name) => {
              if (name) onRenamed(name);
              setVersion((value) => value + 1);
            }}
          />
        )
      )}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <label className="lw-field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

function WorkspacePanel({ data, landlordId, workspaceId, tab, onSaved }) {
  const workspace = data.workspace;
  const [form, setForm] = useState(() => ({
    name: workspace.name || "",
    customLinkEnabled: Boolean(workspace.customLinkEnabled),
    customLinkSlug: workspace.customLinkSlug || "",
    tenant_requirements: normalizeTenantRequirements(
      workspace.tenant_requirements,
    ),
    requirements_config: normalizeRequirementsConfig(
      workspace.requirements_config,
    ),
    insurance_service_status: Boolean(workspace.insurance_service_status),
    skor_protect_service: Boolean(workspace.skor_protect_service),
    affidavit_activated: Boolean(workspace.affidavit_activated),
    protected_housing_enabled: Boolean(workspace.protected_housing_enabled),
    tenant_signup: Boolean(data.email_settings.tenant_signup),
    billing_type: workspace.billing_details?.billing_type || "business",
    ...Object.fromEntries(
      [
        "full_name",
        "company_name",
        "tax_id",
        "country",
        "city",
        "address",
        "postal_code",
      ].map((key) => [key, workspace.billing_details?.[key] || ""]),
    ),
  }));
  const [image, setImage] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [avatar, setAvatar] = useState(null);
  const [localPreview, setLocalPreview] = useState(null);
  useEffect(
    () => () => localPreview && URL.revokeObjectURL(localPreview),
    [localPreview],
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const update = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));

  async function save(event) {
    event.preventDefault();
    setError("");
    setSaving(true);
    const keys = {
      general: ["name"],
      invitations: ["customLinkEnabled", "customLinkSlug"],
      requirements: [
        "tenant_requirements",
        "requirements_config",
        "insurance_service_status",
        "skor_protect_service",
        "affidavit_activated",
        "protected_housing_enabled",
      ],
      notifications: ["tenant_signup"],
      billing: [
        "billing_type",
        "full_name",
        "company_name",
        "tax_id",
        "country",
        "city",
        "address",
        "postal_code",
      ],
    };
    if (tab === "billing")
      keys.billing = keys.billing.filter(
        (key) =>
          !(
            form.billing_type === "business"
              ? ["full_name"]
              : ["company_name", "tax_id"]
          ).includes(key),
      );
    const settings = Object.fromEntries(
      keys[tab].map((key) => [key, form[key]]),
    );
    if (tab === "general") {
      settings.name = settings.name.trim();
    }
    try {
      let body = settings;
      if (["general", "invitations"].includes(tab)) {
        body = new FormData();
        body.append(
          "settings",
          JSON.stringify({
            ...settings,
            removeImage,
            ...(avatar && { imageUrl: `DefaultWorkspace${avatar}.png` }),
          }),
        );
        if (image) body.append("image", image);
      }
      await workspaceRequest(landlordId, `/${workspaceId}/${tab}`, {
        method: "PATCH",
        data: body,
      });
      toast.success("Workspace settings saved");
      onSaved(tab === "general" ? settings.name : null);
    } catch (error) {
      setError(workspaceError(error));
    } finally {
      setSaving(false);
    }
  }

  if (tab === "members")
    return (
      <MembersPanel
        data={data}
        landlordId={landlordId}
        workspaceId={workspaceId}
        onSaved={onSaved}
      />
    );
  const titles = {
    general: "Workspace details",
    invitations: "Invitation branding",
    requirements: "Tenant requirements",
    notifications: "Landlord email preferences",
    billing: "Billing information",
  };
  const existingImage =
    tab === "general" ? workspace.workspaceImage : workspace.invitationLogo;
  const letter = (form.name || workspace.name || "W").slice(0, 1).toUpperCase();
  const preview = image
    ? localPreview
    : avatar
      ? `/images/DefaultWorkspace${avatar}.png`
      : existingImage && !removeImage
        ? imageUrl(existingImage)
        : null;
  return (
    <form className="lw-settings-form" onSubmit={save}>
      <fieldset disabled={saving} className="lw-settings-card">
        <h2>{titles[tab]}</h2>
        {tab === "general" && (
          <>
            <Field
              label="Workspace’s name"
              hint="You can use your organization or company name."
            >
              <input
                required
                maxLength={150}
                value={form.name}
                onChange={(event) => update("name", event.target.value)}
              />
            </Field>
          </>
        )}
        {["general", "invitations"].includes(tab) && (
          <div className="lw-picture-field">
            <strong>{tab === "general" ? "Icon" : "Invitation logo"}</strong>
            <p className="lw-panel-note">
              {tab === "general"
                ? "Upload an image or pick an emoji for profile picture. It will show up in sidebar and notification"
                : "PNG or JPEG."}
            </p>
            <div className="lw-picture">
              <div
                className={`lw-picture-preview ${preview ? "" : "is-letter"}`}
              >
                {preview ? <img src={preview} alt="" /> : <span>{letter}</span>}
              </div>
              <label className="lw-button lw-secondary">
                <Upload size={16} aria-hidden="true" /> Upload logo
                <input
                  hidden
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(event) => {
                    const file = event.target.files[0] || null;
                    setImage(file);
                    setLocalPreview(file ? URL.createObjectURL(file) : null);
                    setRemoveImage(false);
                    setAvatar(null);
                  }}
                />
              </label>
            </div>
            {tab === "general" && (
              <div className="lw-avatars">
                <button
                  type="button"
                  aria-label="Use the workspace initial"
                  aria-pressed={!preview}
                  className={`lw-avatar lw-avatar-letter ${preview ? "" : "is-selected"}`}
                  onClick={() => {
                    setImage(null);
                    setAvatar(null);
                    setRemoveImage(true);
                  }}
                >
                  {letter}
                </button>
                {AVATARS.map((index) => (
                  <button
                    type="button"
                    key={index}
                    aria-label={`Default avatar ${index}`}
                    aria-pressed={avatar === index}
                    className={`lw-avatar ${avatar === index ? "is-selected" : ""}`}
                    onClick={() => {
                      setAvatar(index);
                      setImage(null);
                      setRemoveImage(false);
                    }}
                  >
                    <img src={`/images/DefaultWorkspace${index}.png`} alt="" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {tab === "invitations" && (
          <>
            <Toggle
              label="Custom invitation link"
              description="Use a custom slug for this workspace’s invitation link."
              checked={form.customLinkEnabled}
              onChange={(value) => update("customLinkEnabled", value)}
            />
            {form.customLinkEnabled && (
              <Field
                label="Custom link slug"
                hint="3–15 letters, numbers, hyphens or underscores."
              >
                <input
                  required
                  minLength={3}
                  maxLength={15}
                  pattern="[a-zA-Z0-9_-]{3,15}"
                  value={form.customLinkSlug}
                  onChange={(event) =>
                    update("customLinkSlug", event.target.value.trim())
                  }
                />
              </Field>
            )}
          </>
        )}
        {tab === "requirements" && (
          <>
            <WorkspaceRequirementsConfig
              form={form}
              onChange={(patch) =>
                setForm((current) => ({ ...current, ...patch }))
              }
            />
          </>
        )}
        {tab === "notifications" && (
          <>
            <h3 className="lw-section-title">
              Email preferences in this workspace
            </h3>
            <p className="lw-info-note">
              <Info size={18} aria-hidden="true" />
              In-app notifications are always on. These settings only affect
              emails sent to this landlord for this workspace.
            </p>
            {!data.can_edit_notifications && (
              <p role="status">
                The landlord has no membership record in this workspace, so
                their email preferences cannot be changed.
              </p>
            )}
            <Toggle
              label="Application emails"
              description="Receive an email when a candidature is shared with this workspace or one of its members updates their profile"
              checked={form.tenant_signup}
              disabled={!data.can_edit_notifications}
              onChange={(value) => update("tenant_signup", value)}
            />
          </>
        )}
        {tab === "billing" && (
          <>
            <h3 className="lw-section-title">Payment details</h3>
            <p className="lw-panel-note">
              Invoices for this workspace are issued to these details.
            </p>
            <nav className="lw-tabs lw-tabs-sm" aria-label="Billing type">
              {[
                ["private", "Private"],
                ["business", "Business"],
              ].map(([value, label]) => (
                <button
                  type="button"
                  key={value}
                  aria-pressed={form.billing_type === value}
                  className={form.billing_type === value ? "is-active" : ""}
                  onClick={() => update("billing_type", value)}
                >
                  {label}
                </button>
              ))}
            </nav>
            <div className="lw-field-grid">
              {[
                ...(form.billing_type === "business"
                  ? [
                      ["company_name", "Company name", "Enter company name"],
                      ["tax_id", "Tax ID", "Enter tax ID"],
                    ]
                  : [["full_name", "Full name", "Enter full name"]]),
                ["country", "Country", "Enter country"],
                ["city", "City", "Enter city"],
                ["address", "Address", "Enter address"],
                ["postal_code", "Postal code", "Enter postal code"],
              ].map(([key, label, placeholder]) => (
                <Field
                  label={label}
                  key={key}
                  hint={
                    key === "postal_code" &&
                    form.postal_code &&
                    form.postal_code.length !== 5
                      ? "Postal code must be 5 digits"
                      : undefined
                  }
                >
                  <input
                    required
                    placeholder={placeholder}
                    maxLength={key === "postal_code" ? 5 : 500}
                    inputMode={key === "postal_code" ? "numeric" : undefined}
                    pattern={key === "postal_code" ? "[0-9]{5}" : undefined}
                    value={form[key]}
                    onChange={(event) =>
                      update(
                        key,
                        key === "postal_code"
                          ? event.target.value.replace(/\D/g, "")
                          : event.target.value,
                      )
                    }
                  />
                </Field>
              ))}
            </div>
          </>
        )}
        {error && (
          <p className="lw-error" role="alert">
            {error}
          </p>
        )}
        <div className="lw-form-actions">
          <button
            type="submit"
            className="lw-button lw-primary"
            disabled={
              saving ||
              (tab === "notifications" && !data.can_edit_notifications)
            }
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </fieldset>
    </form>
  );
}

function MembersPanel({ data, landlordId, workspaceId, onSaved }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("BRONZE");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [removing, setRemoving] = useState(null);
  async function mutate(path, method, body) {
    setBusy(true);
    setError("");
    try {
      await workspaceRequest(landlordId, `/${workspaceId}/members${path}`, {
        method,
        data: body,
      });
      toast.success("Workspace members updated");
      onSaved();
    } catch (error) {
      setError(workspaceError(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="lw-settings-card">
      <h2>Workspace members</h2>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          mutate("", "POST", { email: email.trim().toLowerCase(), role });
        }}
      >
        <fieldset disabled={busy} className="lw-invite-form">
          <Field label="Email address">
            <input
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="member@company.com"
            />
          </Field>
          <Field label="Role">
            <select
              value={role}
              onChange={(event) => setRole(event.target.value)}
            >
              {ROLES.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </Field>
          <button type="submit" className="lw-button lw-primary">
            Send invitation
          </button>
        </fieldset>
      </form>
      {error && (
        <p role="alert" className="lw-error">
          {error}
        </p>
      )}
      <ul className="lw-members">
        {data.members.map((member) => {
          const owner =
            member.role === "OWNER" ||
            member.landlordId?._id === data.workspace.created_by;
          const name =
            [member.landlordId?.fname_companyName, member.landlordId?.lname]
              .filter(Boolean)
              .join(" ") ||
            member.email ||
            "Guest";
          return (
            <li key={member._id}>
              <span className="lw-workspace-icon">
                {name.slice(0, 1).toUpperCase()}
              </span>
              <div className="lw-member-identity">
                <strong>{name}</strong>
                <small>{member.landlordId?.email || member.email}</small>
                <small>{member.status}</small>
              </div>
              {owner || member.role === "GUEST" ? (
                <span className="lw-count">{member.role}</span>
              ) : (
                <select
                  aria-label={`Role for ${name}`}
                  value={member.role}
                  disabled={busy}
                  onChange={(event) =>
                    mutate(`/${member._id}`, "PATCH", {
                      role: event.target.value,
                    })
                  }
                >
                  {ROLES.filter((value) => value !== "GUEST").map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              )}
              {!owner && (
                <button
                  className="lw-button"
                  type="button"
                  disabled={busy}
                  aria-label={`Remove ${name}`}
                  onClick={() => setRemoving(member._id)}
                >
                  <Trash2 size={16} />
                </button>
              )}
              {removing === member._id && (
                <div className="lw-remove-confirm">
                  <p>Remove {name} from this workspace?</p>
                  <button
                    className="lw-button"
                    disabled={busy}
                    type="button"
                    onClick={() => setRemoving(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="lw-button"
                    disabled={busy}
                    type="button"
                    onClick={() => mutate(`/${member._id}`, "DELETE")}
                  >
                    Remove member
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {data.members.length === 0 && (
        <p className="lw-panel-note">No workspace members found.</p>
      )}
      <p className="lw-panel-note">
        <LayoutGrid size={14} /> The workspace owner cannot be removed or
        reassigned.
      </p>
    </div>
  );
}
